<script lang="ts">
  import type {
    Alternative,
    ClosingState,
    OpenState,
  } from '@pin/schemas/client-state-response';
  import { onMount } from 'svelte';
  import { postvoteWithRetries } from '../api/post-vote';
  import AlternativeItem from '../components/AlternativeItem.svelte';
  import AlternativeList from '../components/AlternativeList.svelte';
  import Receipt from '../components/Receipt.svelte';
  import VotingSlip from '../components/VotingSlip.svelte';
  import { pushAria } from '../util/ariaLive';
  import { i18n } from '../util/i18n/i18n';
  import { captureError } from '../util/sentry';
  import { trackEvent } from '../util/snowplow';
  import { getItem, removeItem, setItem } from '../util/storage';
  import {
    hasSeenSubmitTooltip,
    votingSlipTooltip,
  } from '../util/tooltipstore';
  import { getStoredVotesKey, getSubmittedVotesKey } from '../util/votesKeys';

  import { dispatchConfetti } from '../util/confetti';

  export let pollData: OpenState | ClosingState;
  $: submittedVotesKey = getSubmittedVotesKey(pollData.id);
  $: storedVotesKey = getStoredVotesKey(pollData.id);

  $: submitted = !!getItem<string[]>('local', submittedVotesKey);
  $: votes =
    getItem<string[]>('local', submittedVotesKey) ??
    getItem<string[]>('session', storedVotesKey) ??
    [];

  let submitting = false;

  const addVote = (alternativeId: string) => {
    votes = [...votes, alternativeId];
    setItem('session', storedVotesKey, votes);
    const alternativeTitle =
      pollData.alternatives.filter((a) => a.id === alternativeId)[0].title ??
      i18n('aria-live.alternative');
    if (votes.length === pollData.maxVotes) {
      pushAria('aria-live.add-last-vote', {
        values: {
          title: alternativeTitle,
          maxVotes: pollData.maxVotes,
        },
      });
    } else {
      pushAria('aria-live.add-vote', {
        values: {
          title: alternativeTitle,
          votesLeft: pollData.maxVotes - votes.length,
        },
      });
    }
    trackEvent(
      { action: 'click:add-vote', label: pollData.title },
      pollData.id
    );
  };

  const removeVote = (removeIndex: number) => () => {
    const alternativeId = votes[removeIndex];
    votes = votes.filter((_alternative, index) => index !== removeIndex);
    setItem('session', storedVotesKey, votes);
    const alternativeTitle =
      pollData.alternatives.filter((a) => a.id === alternativeId)[0].title ??
      i18n('aria-live.alternative');

    pushAria('aria-live.remove-vote', { values: { title: alternativeTitle } });
    trackEvent(
      { action: 'click:remove-vote', label: pollData.title },
      pollData.id
    );
  };

  const submit = async () => {
    const allVotesSelected = votes.length === pollData.maxVotes;
    if (!allVotesSelected) {
      trackEvent(
        { action: 'click:submit-votes-disabled', label: pollData.title },
        pollData.id
      );
      votingSlipTooltip.set({
        id: 'votingslip',
        message: i18n('poll.tooltip.vote-count', {
          values: { votesLeft: pollData.maxVotes - votes.length },
        }),
      });
      return;
    }

    submitting = true;
    votingSlipTooltip.set(undefined);
    hasSeenSubmitTooltip.set(true);
    trackEvent(
      { action: 'click:submit-votes', label: pollData.title },
      pollData.id
    );
    await tryPostVote();
    try {
      setItem('local', submittedVotesKey, votes);
      removeItem('session', storedVotesKey);
      trackEvent(
        { action: 'success:submit', label: pollData.title },
        pollData.id
      );
      submitted = true;
      pushAria('aria-live.submitted');
      dispatchConfetti(500);
    } catch (e) {
      // We saw some weird error:submit events
      // in snowplow coming in milliseconds after success:submit
      // so I want to log this and see if we can find out what it was.
      trackEvent({ action: 'error:weird-submit' }, pollData.id);
      captureError('error:weird-submit', e);
    } finally {
      submitting = false;
    }
  };

  const tryPostVote = async () => {
    try {
      await postvoteWithRetries(pollData.voteUrl, {
        pollId: pollData.id,
        systemId: 'generic-client',
        alternativeIds: votes,
      });
    } catch (e) {
      trackEvent(
        { action: 'error:submit', label: pollData.title },
        pollData.id
      );
      votingSlipTooltip.set({
        id: 'votingslip',
        message: i18n('poll.tooltip.send-failed', {
          values: { maxVotes: pollData.maxVotes },
        }),
      });
    }
  };

  $: votesLeft = pollData.maxVotes - votes.length;
  $: alternatives = pollData.alternatives.reduce<Record<string, Alternative>>(
    (prevValue, alternative) => ({
      ...prevValue,
      [alternative.id]: alternative,
    }),
    {}
  );

  onMount(() => {
    trackEvent(
      { action: 'state:open-poll', label: pollData.title },
      pollData.id
    );
  });
</script>

{#if submitted}
  <!-- svelte-ignore a11y-autofocus -->
  <h2 autofocus tabindex="-1">{i18n('poll.thanks-for-vote')}</h2>
  <p>
    {i18n('poll.has-submitted', { values: { maxVotes: pollData.maxVotes } })}
  </p>
  <Receipt alternatives={pollData.alternatives} {votes} />
{:else}
  <!-- svelte-ignore a11y-autofocus -->
  <h2 autofocus tabindex="-1">{i18n('poll.vote-heading')}</h2>
  <h2>
    {i18n('poll.votes-left1')}
    <span class="number">{votesLeft}</span>
    {i18n('poll.votes-left2', {
      values: { votesLeft },
    })}
  </h2>
  <AlternativeList>
    {#each pollData.alternatives as alternative}
      <AlternativeItem
        disabled={submitting || votes.length === pollData.maxVotes}
        {alternative}
        {addVote}
        {votes}
        {submitted}
      />
    {/each}
  </AlternativeList>
{/if}
{#if !submitted}
  <VotingSlip
    {submitting}
    {votes}
    {alternatives}
    {submit}
    {removeVote}
    maxVotes={pollData.maxVotes}
  />
{/if}

<style>
  .number {
    color: var(--sk-pink);
  }
</style>
